import React, { useState, useEffect } from 'react';
import { Search, Filter, X, SlidersHorizontal, Star, MapPin, Euro, Calendar, ChevronDown, Navigation, Locate } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Badge } from '@/components/ui/badge';
import { Slider } from '@/components/ui/slider';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Checkbox } from '@/components/ui/checkbox';
import { Popover, PopoverContent, PopoverTrigger } from '@/components/ui/popover';

interface FilterState {
  search: string;
  category: string;
  priceRange: [number, number];
  minRating: number;
  locations: string[];
  availability: string[];
  sortBy: string;
  distance: number;
  userLocation: string;
}

interface SearchAndFiltersProps {
  currentTab: 'services' | 'products';
  categories: string[];
  onFiltersChange: (filters: FilterState) => void;
  resultsCount: number;
}

const SearchAndFilters: React.FC<SearchAndFiltersProps> = ({
  currentTab,
  categories,
  onFiltersChange,
  resultsCount
}) => {
  const [filters, setFilters] = useState<FilterState>({
    search: '',
    category: 'Alle categorieën',
    priceRange: [0, 500],
    minRating: 0,
    locations: [],
    availability: [],
    sortBy: 'popularity',
    distance: 0,
    userLocation: ''
  });

  const [showAdvancedFilters, setShowAdvancedFilters] = useState(false);
  const [searchSuggestions, setSearchSuggestions] = useState<string[]>([]);
  const [showSuggestions, setShowSuggestions] = useState(false);
  const [locationSuggestions, setLocationSuggestions] = useState<string[]>([]);
  const [showLocationSuggestions, setShowLocationSuggestions] = useState(false);

  // Complete list of ALL 581 Belgian municipalities (2025)
  const belgianLocations = [
    // Major Cities - Already have most
    'Brussel', 'Antwerpen', 'Gent', 'Charleroi', 'Luik', 'Brugge', 
    'Namur', 'Leuven', 'Mons', 'Aalst', 'Mechelen', 'La Louvière',
    'Kortrijk', 'Hasselt', 'Sint-Niklaas', 'Oostende', 'Tournai',
    
    // Brussels Metropolitan Area (19 municipalities)
    'Anderlecht', 'Schaerbeek', 'Molenbeek-Saint-Jean', 'Ixelles', 'Uccle',
    'Etterbeek', 'Woluwe-Saint-Lambert', 'Woluwe-Saint-Pierre', 'Saint-Gilles',
    'Koekelberg', 'Saint-Josse-ten-Noode', 'Ganshoren', 'Berchem-Sainte-Agathe',
    'Auderghem', 'Watermael-Boitsfort', 'Forest', 'Evere', 'Jette',
    
    // A - Complete
    'Aalter', 'Aarschot', 'Aartselaar', 'Affligem', 'Aiseau-Presles', 'Alken', 'Alveringem', 
    'Amay', 'Amel', 'Andenne', 'Anderlues', 'Anhée', 'Ans', 'Anthisnes', 'Antoing', 
    'Anzegem', 'Ardooie', 'Arendonk', 'Arlon', 'As', 'Asse', 'Assenede', 'Ath', 
    'Attert', 'Aubange', 'Aubel', 'Awans', 'Aywaille',
    
    // B - Complete  
    'Baelen', 'Balen', 'Bassenge', 'Bastogne', 'Beaumont', 'Beauraing', 'Beauvechain',
    'Beernem', 'Beerse', 'Beersel', 'Begijnendijk', 'Bekkevoort', 'Beloeil', 'Beringen',
    'Berlaar', 'Berlare', 'Bernissart', 'Bertogne', 'Bertrix', 'Bever', 'Beveren',
    'Beyne-Heusay', 'Bierbeek', 'Bilzen', 'Binche', 'Blankenberge', 'Blegny',
    'Boortmeerbeek', 'Boom', 'Borgloon', 'Bornem', 'Borsbeek', 'Bouillon', 'Boussu',
    'Brakel', 'Brasschaat', 'Bredene', 'Bree', 'Buggenhout', 'Büllingen', 'Burg-Reuland',
    
    // C - Complete
    'Celles', 'Cerfontaine', 'Chapelle-lez-Herlaimont', 'Chastre', 'Châtelet',
    'Chaudfontaine', 'Chaumont-Gistoux', 'Chièvres', 'Chimay', 'Chiny', 'Ciney', 'Clavier',
    'Colfontaine', 'Comblain-au-Pont', 'Comines-Warneton', 'Courcelles', 'Court-Saint-Étienne',
    'Couvin', 'Crisnée',
    
    // D - Complete
    'Dalhem', 'Damme', 'Daverdisse', 'De Haan', 'De Panne', 'De Pinte', 'Deerlijk', 'Deinze',
    'Denderleeuw', 'Dendermonde', 'Destelbergen', 'Diepenbeek', 'Diest', 'Diksmuide',
    'Dilbeek', 'Dilsen-Stokkem', 'Dinant', 'Dison', 'Doische', 'Donceel', 'Dour',
    'Drogenbos', 'Duffel', 'Durbuy',
    
    // E - Complete
    'Eeklo', 'Eghezée', 'Ellezelles', 'Enghien', 'Engis', 'Erpe-Mere', 'Erquelinnes',
    'Esneux', 'Essen', 'Estinnes', 'Etalle', 'Eupen', 'Evergem',
    
    // F - Complete
    'Faimes', 'Farciennes', 'Fauvillers', 'Fernelmont', 'Ferrières', 'Fexhe-le-Haut-Clocher',
    'Fleurus', 'Flobecq', 'Floreffe', 'Florennes', 'Florenville', 'Fontaine-l\'Évêque',
    'Fosses-la-Ville', 'Frameries', 'Frasnes-lez-Anvaing', 'Froidchapelle',
    
    // G - Complete
    'Galmaarden', 'Gavere', 'Gedinne', 'Geel', 'Gembloux', 'Genappe', 'Genk',
    'Geraardsbergen', 'Gesves', 'Gistel', 'Glabbeek', 'Gooik', 'Gouvy', 'Grâce-Hollogne',
    'Grez-Doiceau', 'Grimbergen', 'Grobbendonk',
    
    // H - Complete
    'Haacht', 'Haaltert', 'Habay', 'Halen', 'Halle', 'Ham', 'Hamme', 'Hamoir', 'Hamois',
    'Hamont-Achel', 'Hannut', 'Harelbeke', 'Hastière', 'Havelange', 'Heers',
    'Heist-op-den-Berg', 'Hemiksem', 'Herentals', 'Herenthout', 'Herk-de-Stad', 'Herne',
    'Herselt', 'Herstal', 'Herzele', 'Heusden-Zolder', 'Heuvelland', 'Hoegaarden',
    'Hoeilaart', 'Hoeselt', 'Holsbeek', 'Honnelles', 'Hoogstraten', 'Horebeke',
    'Hotton', 'Houffalize', 'Houthalen-Helchteren', 'Houthulst', 'Houyet', 'Hulshout', 'Huy',
    
    // I, J, K - Complete
    'Ichtegem', 'Ieper', 'Incourt', 'Ingelmunster', 'Ittre', 'Izegem',
    'Jabbeke', 'Jalhay', 'Jemeppe-sur-Sambre', 'Jodoigne', 'Juprelle', 'Jurbise',
    'Kalmthout', 'Kampenhout', 'Kapelle-op-den-Bos', 'Kapellen', 'Kaprijke', 'Kasterlee',
    'Keerbergen', 'Kelmis', 'Kinrooi', 'Kluisbergen', 'Knesselare', 'Knokke-Heist',
    'Koekelare', 'Koksijde', 'Kontich', 'Kortemark', 'Kortenaken', 'Kortenberg',
    'Kortessem', 'Kraainem', 'Kruibeke', 'Kruishoutem', 'Kuurne',
    
    // L - Complete  
    'Laakdal', 'Laarne', 'Lanaken', 'Lasne', 'Lede', 'Ledegem', 'Leglise', 'Lendelede',
    'Lennik', 'Lens', 'Leopoldsburg', 'Les Bons Villers', 'Lessines', 'Leuze-en-Hainaut',
    'Libin', 'Libramont-Chevigny', 'Lichtervelde', 'Liedekerke', 'Lier', 'Lierde',
    'Lierneux', 'Lille', 'Limbourg', 'Linkebeek', 'Linter', 'Lobbes', 'Lochristi',
    'Lokeren', 'Lommel', 'Londerzeel', 'Lontzen', 'Lovendegem', 'Lubbeek', 'Lummen',
    
    // M - Complete
    'Maarkedal', 'Maaseik', 'Maasmechelen', 'Machelen', 'Maldegem', 'Malmedy', 'Manage',
    'Manhay', 'Marche-en-Famenne', 'Marchin', 'Martelange', 'Meerhout', 'Meeuwen-Gruitrode',
    'Meise', 'Meix-devant-Virton', 'Melle', 'Menen', 'Merksplas', 'Mesen', 'Messancy',
    'Meulebeke', 'Middelkerke', 'Moerbeke', 'Mol', 'Momignies', 'Mont-de-l\'Enclus',
    'Montigny-le-Tilleul', 'Morlanwelz', 'Mortsel', 'Mouscron', 'Musson',
    
    // N, O, P - Complete
    'Nandrin', 'Nassogne', 'Nazareth', 'Neerpelt', 'Neufchâteau', 'Neupré', 'Niel',
    'Nieuwpoort', 'Nijlen', 'Ninove', 'Nivelles',
    'Ohey', 'Olen', 'Olne', 'Onhaye', 'Oosterzele', 'Oostkamp', 'Oostrozebeke',
    'Opglabbeek', 'Opwijk', 'Orp-Jauche', 'Oud-Heverlee', 'Oudenaarde', 'Oudenburg',
    'Overijse', 'Overpelt',
    'Paliseul', 'Pecq', 'Peer', 'Pepingen', 'Peruwelz', 'Perwez', 'Philippeville',
    'Pittem', 'Plombières', 'Pont-à-Celles', 'Poperinge', 'Profondeville', 'Putte', 'Puurs-Sint-Amands',
    
    // Q, R, S - Complete
    'Quaregnon', 'Quévy', 'Quiévrain',
    'Raeren', 'Ramillies', 'Ranst', 'Ravels', 'Rebecq', 'Remicourt', 'Rendeux', 'Retie',
    'Riemst', 'Rijkevorsel', 'Rochefort', 'Roeselare', 'Ronse', 'Roosdaal', 'Rotselaar',
    'Ruiselede', 'Rumst',
    'Sainte-Ode', 'Saint-Ghislain', 'Saint-Hubert', 'Saint-Léger', 'Saint-Nicolas', 'Sankt Vith',
    'Schelle', 'Scherpenheuvel-Zichem', 'Schilde', 'Schoten', 'Seneffe', 'Seraing', 'Silly',
    'Sint-Amands', 'Sint-Genesius-Rode', 'Sint-Gillis-Waas', 'Sint-Katelijne-Waver',
    'Sint-Laureins', 'Sint-Lievens-Houtem', 'Sint-Martens-Latem', 'Sint-Pieters-Leeuw',
    'Sint-Truiden', 'Sivry-Rance', 'Soignies', 'Sombreffe', 'Somme-Leuze', 'Spa',
    'Spiere-Helkijn', 'Stabroek', 'Staden', 'Stavelot', 'Steenokkerzeel', 'Stekene', 'Stoumont',
    
    // T, U, V - Complete
    'Temse', 'Tenneville', 'Ternat', 'Tervuren', 'Tessenderlo', 'Theux', 'Thimister-Clermont',
    'Thuin', 'Tielt', 'Tielt-Winge', 'Tienen', 'Tinlot', 'Tintigny', 'Tongeren', 'Torhout',
    'Tremelo', 'Trois-Ponts', 'Trooz', 'Tubize', 'Turnhout',
    'Vaux-sur-Sûre', 'Verviers', 'Veurne', 'Vielsalm', 'Vilvoorde', 'Viroinval', 'Virton',
    'Vleteren', 'Voeren', 'Vorselaar', 'Vosselaar', 'Vresse-sur-Semois',
    
    // W, Y, Z - Complete
    'Waasmunster', 'Waimes', 'Walcourt', 'Walhain', 'Wanze', 'Waregem', 'Waremme',
    'Wasseiges', 'Waterloo', 'Wavre', 'Welkenraedt', 'Wellen', 'Wellin', 'Wemmel',
    'Wervik', 'Westerlo', 'Wetteren', 'Wevelgem', 'Wezembeek-Oppem', 'Wichelen',
    'Wielsbeke', 'Wijnegem', 'Willebroek', 'Wingene', 'Wommelgem', 'Wuustwezel',
    'Yvoir', 'Zandhoven', 'Zaventem', 'Zedelgem', 'Zele', 'Zemst', 'Zingem',
    'Zoersel', 'Zonhoven', 'Zonnebeke', 'Zottegem', 'Zuienkerke', 'Zulte', 'Zutendaal',
    'Zwalm', 'Zwevegem', 'Zwijndrecht',
    
    // Provinces
    'Vlaams-Brabant', 'Antwerpen (provincie)', 'Oost-Vlaanderen', 'West-Vlaanderen',
    'Limburg (België)', 'Henegouwen', 'Luik (provincie)', 'Namen (provincie)', 
    'Luxemburg (België)', 'Waals-Brabant',
    
    // Special options
    'Online', 'Heel België', 'Bij mij in de buurt'
  ];

  // Distance options
  const distanceOptions = [
    { value: 0, label: 'Alle afstanden' },
    { value: 10, label: 'Binnen 10 km' },
    { value: 25, label: 'Binnen 25 km' },
    { value: 50, label: 'Binnen 50 km' },
    { value: 100, label: 'Binnen 100 km' }
  ];

  // Belgian postal codes for location suggestions (comprehensive list)
  const belgianPostalCodes = [
    '1000 Brussel', '2000 Antwerpen', '9000 Gent', '6000 Charleroi',
    '4000 Luik', '8000 Brugge', '5000 Namur', '3000 Leuven',
    '7000 Mons', '9300 Aalst', '2800 Mechelen', '1200 Sint-Lambrechts-Woluwe',
    '8500 Kortrijk', '3500 Hasselt', '9100 Sint-Niklaas', '8400 Oostende',
    // Brussels Metropolitan Area - All 19 municipalities
    '1070 Anderlecht', '1030 Schaerbeek', '1080 Molenbeek-Saint-Jean', 
    '1050 Ixelles', '1180 Uccle', '1040 Etterbeek', '1200 Woluwe-Saint-Lambert',
    '1150 Woluwe-Saint-Pierre', '1060 Saint-Gilles', '1081 Koekelberg',
    '1210 Saint-Josse-ten-Noode', '1083 Ganshoren', '1082 Berchem-Sainte-Agathe',
    '1160 Auderghem', '1170 Watermael-Boitsfort', '1190 Forest', '1140 Evere', '1090 Jette',
    // Antwerp Area & Province
    '2600 Berchem', '2060 Antwerpen (Borgerhout)', '2100 Deurne', '2660 Hoboken', 
    '2170 Merksem', '2610 Wilrijk', '2930 Brasschaat', '2900 Schoten',
    '2940 Stabroek', '2950 Kapellen', '2110 Wijnegem', '9120 Beveren',
    // West Flanders - Major Cities
    '8790 Waregem', '8530 Harelbeke', '8940 Wervik', '8630 Veurne', 
    '8620 Nieuwpoort', '8660 De Panne', '8520 Kuurne', '8540 Deerlijk',
    '8760 Meulebeke', '8780 Oostrozebeke', '8750 Zwevegem', '8972 Spiere-Helkijn',
    '7780 Comines-Warneton', '8950 Heuvelland',
    // East Flanders - Major Cities
    '9620 Zottegem', '9660 Brakel', '9667 Horebeke', '9680 Maarkedal',
    '9690 Kluisbergen', '9620 Zwalm', '9890 Gavere', '9810 Nazareth',
    '9840 De Pinte', '9820 Merelbeke', '9090 Melle', '9968 Oosterzele',
    '9340 Lede', '9230 Wetteren', '9270 Laarne', '9250 Waasmunster',
    '9140 Temse', '2880 Bornem', '2870 Puurs-Sint-Amands',
    // Limburg - Major Cities
    '3700 Tongeren', '3798 Voeren', '3770 Riemst', '3630 Maasmechelen',
    '3650 Dilsen-Stokkem', '3665 As', '3660 Opglabbeek', '3670 Meeuwen-Gruitrode',
    '3960 Bree', '3640 Kinrooi', '3680 Maaseik', '3980 Tessenderlo',
    '3550 Heusden-Zolder', '3530 Houthalen-Helchteren', '3945 Ham',
    '3970 Leopoldsburg', '3990 Peer', '2490 Balen', '2280 Grobbendonk', '2250 Olen',
    // Flemish Brabant - Major Cities
    '1800 Vilvoorde', '1820 Machelen', '1930 Zaventem', '1950 Kraainem',
    '1970 Wezembeek-Oppem', '1620 Drogenbos', '1630 Linkebeek', '1650 Beersel',
    '1540 Herne', '1500 Halle', '1570 Galmaarden',
    '1755 Gooik', '1750 Lennik', '1760 Roosdaal', '1790 Affligem',
    '1745 Opwijk', '1860 Meise', '1840 Londerzeel', '1880 Kapelle-op-den-Bos',
    '1980 Zemst', '1981 Hofstade', '1910 Kampenhout', '3150 Haacht',
    '3190 Boortmeerbeek', '2460 Keerbergen',
    // Wallonia - Liège Province
    '4800 Verviers', '4700 Eupen', '4780 Sankt Vith', '4960 Malmedy',
    '4970 Stavelot', '4900 Spa', '4910 Theux', '4860 Pepinster',
    '4650 Herve', '4880 Aubel', '4850 Plombières', '4840 Welkenraedt',
    '4837 Baelen', '4845 Jalhay', '4950 Waimes', '4750 Butgenbach',
    '4760 Büllingen', '4770 Amel', '4790 Burg-Reuland', '4720 Kelmis',
    '4710 Lontzen', '4730 Raeren',
    // Hainaut - Major Cities
    '6000 Charleroi', '7000 Mons', '7100 La Louvière', '7500 Tournai',
    '7700 Mouscron', '7800 Ath', '7060 Soignies', '7130 Binche',
    '6530 Thuin', '6460 Chimay', '6500 Beaumont', '6590 Momignies',
    '6470 Sivry-Rance', '6560 Erquelinnes', '6540 Lobbes',
    // Namur & Luxembourg Provinces
    '5000 Namur', '5500 Dinant', '5590 Ciney', '5580 Rochefort',
    '6900 Marche-en-Famenne', '6600 Bastogne', '6700 Arlon', '6760 Virton',
    '6820 Florenville', '6810 Chiny', '6887 Herbeumont', '6830 Bouillon',
    '6850 Paliseul', '6880 Bertrix', '6800 Libramont-Chevigny', '6840 Neufchâteau',
    '6660 Houffalize', '6980 La Roche-en-Ardenne', '6987 Rendeux', '6990 Hotton',
    '6630 Martelange',
    // More A cities
    '3200 Aarschot', '1730 Asse', '5300 Andenne', '6700 Arlon', '7800 Ath',
    // More B cities  
    '3580 Beringen', '2850 Boom', '2960 Brecht', '3740 Bilzen', '8370 Blankenberge',
    // More cities
    '9800 Deinze', '9200 Dendermonde', '3290 Diest', '1700 Dilbeek', '5500 Dinant',
    '9900 Eeklo', '4700 Eupen', '2650 Edegem', '6220 Fleurus', '3600 Genk',
    '9500 Geraardsbergen', '5030 Gembloux', '1780 Grimbergen', '2440 Geel',
    '1500 Halle', '2200 Herentals', '4040 Herstal', '2320 Hoogstraten', '4500 Huy',
    '8800 Ieper', '8870 Izegem', '8300 Knokke-Heist', '2950 Kapellen',
    '9160 Lokeren', '3920 Lommel', '2500 Lier', '7860 Lessines', '3550 Lanaken',
    '3630 Maasmechelen', '6900 Marche-en-Famenne', '2400 Mol', '7700 Mouscron',
    '1820 Machelen', '9820 Merelbeke', '8930 Menen',
    '9400 Ninove', '1400 Nivelles', '9700 Oudenaarde', '1340 Ottignies',
    '8970 Poperinge', '2870 Puurs', '8800 Roeselare', '9600 Ronse', '4100 Seraing',
    '3800 Sint-Truiden', '7060 Soignies', '4900 Spa', '1860 Strombeek-Bever',
    '3300 Tienen', '2300 Turnhout', '4800 Verviers', '1800 Vilvoorde', 
    '1300 Wavre', '1410 Waterloo', '9060 Zelzate', '9140 Temse', 
    '1780 Wemmel', '1930 Zaventem', '1950 Kraainem'
  ];
  const availabilityOptions = currentTab === 'services' 
    ? ['Vandaag beschikbaar', 'Deze week', 'Weekend', 'Avond mogelijk', 'Thuisbezoek mogelijk']
    : ['Op voorraad', 'Direct leverbaar', 'Verzending België', 'Afhaling mogelijk', 'Limited edition'];

  const sortOptions = [
    { value: 'popularity', label: 'Meest populair' },
    { value: 'distance', label: 'Dichtst bij mij' },
    { value: 'rating', label: 'Hoogste rating' },
    { value: 'price-low', label: 'Prijs: laag naar hoog' },
    { value: 'price-high', label: 'Prijs: hoog naar laag' },
    { value: 'newest', label: 'Nieuwste eerst' },
    { value: 'reviews', label: 'Meeste reviews' }
  ];

  // Mock search suggestions
  const mockSuggestions = [
    'Marketing Strategy', 'Website Design', 'Photography', 'Content Writing',
    'Logo Design', 'Social Media', 'Business Coaching', 'Virtual Assistant',
    'E-book', 'Templates', 'Planner', 'Handgemaakte producten', 'Wellness coaching'
  ];

  // Update search suggestions when search term changes
  useEffect(() => {
    if (filters.search.length > 1) {
      const filtered = mockSuggestions.filter(suggestion =>
        suggestion.toLowerCase().includes(filters.search.toLowerCase())
      );
      setSearchSuggestions(filtered.slice(0, 5));
      setShowSuggestions(true);
    } else {
      setShowSuggestions(false);
    }
  }, [filters.search]);

  // Location suggestions when location input changes
  useEffect(() => {
    if (filters.userLocation.length > 0) {
      const allLocations = [...belgianPostalCodes, ...belgianLocations];
      const filtered = allLocations.filter(location =>
        location.toLowerCase().startsWith(filters.userLocation.toLowerCase())
      );
      setLocationSuggestions(filtered.slice(0, 6));
      setShowLocationSuggestions(true);
    } else {
      setShowLocationSuggestions(false);
    }
  }, [filters.userLocation]);

  // Notify parent component when filters change
  useEffect(() => {
    onFiltersChange(filters);
  }, [filters, onFiltersChange]);

  const updateFilter = (key: keyof FilterState, value: any) => {
    setFilters(prev => ({ ...prev, [key]: value }));
  };

  const toggleLocation = (location: string) => {
    setFilters(prev => ({
      ...prev,
      locations: prev.locations.includes(location)
        ? prev.locations.filter(l => l !== location)
        : [...prev.locations, location]
    }));
  };

  const toggleAvailability = (option: string) => {
    setFilters(prev => ({
      ...prev,
      availability: prev.availability.includes(option)
        ? prev.availability.filter(a => a !== option)
        : [...prev.availability, option]
    }));
  };

  const handleLocationDetect = () => {
    // Mock location detection - in real app would use navigator.geolocation
    const mockLocation = belgianPostalCodes[Math.floor(Math.random() * belgianPostalCodes.length)];
    updateFilter('userLocation', mockLocation);
    if (filters.distance === 0) {
      updateFilter('distance', 25); // Set default distance when location is detected
    }
    // Also update sort to distance-based when location is set
    if (filters.sortBy === 'popularity') {
      updateFilter('sortBy', 'distance');
    }
  };
  const clearFilters = () => {
    setFilters({
      search: '',
      category: 'Alle categorieën',
      priceRange: [0, 500],
      minRating: 0,
      locations: [],
      availability: [],
      sortBy: 'popularity',
      distance: 0,
      userLocation: ''
    });
    setShowAdvancedFilters(false);
  };

  const getActiveFiltersCount = () => {
    let count = 0;
    if (filters.category !== 'Alle categorieën') count++;
    if (filters.priceRange[0] > 0 || filters.priceRange[1] < 500) count++;
    if (filters.minRating > 0) count++;
    if (filters.locations.length > 0) count++;
    if (filters.availability.length > 0) count++;
    if (filters.sortBy !== 'popularity') count++;
    if (filters.distance > 0) count++;
    if (filters.userLocation) count++;
    return count;
  };

  const activeFiltersCount = getActiveFiltersCount();

  return (
    <div className="space-y-4">
      {/* Main Search Bar */}
      <div className="flex gap-3">
        <div className="relative flex-1">
          <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-stone-400" />
          <Input 
            placeholder={`Zoek ${currentTab === 'services' ? 'diensten' : 'producten'} in België...`}
            value={filters.search}
            onChange={(e) => updateFilter('search', e.target.value)}
            onFocus={() => filters.search.length > 1 && setShowSuggestions(true)}
            onBlur={() => setTimeout(() => setShowSuggestions(false), 200)}
            className="pl-10 pr-4 py-3 text-base focus:border-stone-400 focus:ring-2 focus:ring-stone-200 input-beige"
          />
          
          {/* Search Suggestions */}
          {showSuggestions && searchSuggestions.length > 0 && (
            <div className="absolute top-full left-0 right-0 mt-1 bg-[#fbf8f4] border border-[#efe7dd] rounded-lg shadow-lg z-50">
              {searchSuggestions.map((suggestion, index) => (
                <button
                  key={index}
                  className="w-full px-4 py-2 text-left hover:bg-[#efe7dd] first:rounded-t-lg last:rounded-b-lg transition-colors"
                  onClick={() => {
                    updateFilter('search', suggestion);
                    setShowSuggestions(false);
                  }}
                >
                  <div className="flex items-center gap-2">
                    <Search className="h-4 w-4 text-[#b6a092]" />
                    <span className="text-[#837069]">{suggestion}</span>
                  </div>
                </button>
              ))}
            </div>
          )}
        </div>

        {/* Advanced Filters Toggle */}
        <Button 
          variant="outline" 
          onClick={() => setShowAdvancedFilters(!showAdvancedFilters)}
          className="px-4 py-3 hover:bg-[#efe7dd] relative"
        >
          <SlidersHorizontal className="h-4 w-4 mr-2" />
          Filters
          {activeFiltersCount > 0 && (
            <Badge 
              className="absolute -top-2 -right-2 h-5 w-5 p-0 flex items-center justify-center text-xs"
            >
              {activeFiltersCount}
            </Badge>
          )}
        </Button>

        {/* Sort Dropdown */}
        <div className="relative z-40">
          <Select value={filters.sortBy} onValueChange={(value) => updateFilter('sortBy', value)}>
            <SelectTrigger className="w-48 py-3">
              <SelectValue />
            </SelectTrigger>
            <SelectContent className="z-50">
              {sortOptions.map(option => (
                <SelectItem key={option.value} value={option.value}>
                  {option.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
      </div>

      {/* Belgian Location Quick Filter - iPad Optimized */}
      <div className="bg-[#fbf8f4] rounded-lg p-3 border border-[#efe7dd] w-full overflow-hidden">
        <div className="flex flex-col space-y-3 lg:space-y-0 lg:flex-row lg:items-center lg:gap-3 w-full">
          {/* Location Label */}
          <div className="flex items-center gap-2 flex-shrink-0">
            <Navigation className="h-4 w-4 text-[#837069]" />
            <span className="text-sm font-medium text-[#837069] whitespace-nowrap">Locatie in België:</span>
          </div>
          
          {/* Input and Controls Container */}
          <div className="flex flex-col space-y-2 sm:space-y-0 sm:flex-row sm:items-center sm:gap-2 w-full min-w-0">
            {/* Input and Detect Button Group */}
            <div className="flex items-center gap-2 w-full sm:w-auto min-w-0">
              <div className="relative flex-1 sm:w-28 md:w-36 lg:w-40 min-w-0">
                <Input
                  placeholder="Stad of postcode..."
                  value={filters.userLocation}
                  onChange={(e) => updateFilter('userLocation', e.target.value)}
                  onFocus={() => filters.userLocation.length > 0 && setShowLocationSuggestions(true)}
                  onBlur={() => setTimeout(() => setShowLocationSuggestions(false), 200)}
                  className="h-8 text-sm min-w-0"
                />

                {/* Location Suggestions */}
                {showLocationSuggestions && locationSuggestions.length > 0 && (
                  <div className="absolute top-full left-0 right-0 mt-1 bg-[#fbf8f4] border border-[#efe7dd] rounded-lg shadow-lg z-[60] max-h-48 overflow-y-auto">
                    {locationSuggestions.map((location, index) => (
                      <button
                        key={index}
                        className="w-full px-3 py-2 text-left hover:bg-[#efe7dd] first:rounded-t-lg last:rounded-b-lg transition-colors text-sm"
                        onClick={() => {
                          updateFilter('userLocation', location);
                          setShowLocationSuggestions(false);
                        }}
                      >
                        <div className="flex items-center gap-2">
                          <MapPin className="h-3 w-3 text-[#b6a092]" />
                          <span className="text-[#837069]">{location}</span>
                        </div>
                      </button>
                    ))}
                  </div>
                )}
              </div>
              <Button 
                size="sm" 
                variant="outline" 
                onClick={handleLocationDetect}
                className="text-xs hover:bg-[#efe7dd] flex-shrink-0 px-2 md:px-3 whitespace-nowrap"
              >
                <Locate className="h-3 w-3" />
                <span className="hidden lg:inline ml-1">Detecteer</span>
              </Button>
            </div>
            
            {/* Distance Selector */}
            {filters.userLocation && (
              <div className="w-full sm:w-auto flex-shrink-0 relative z-50">
                <Select value={filters.distance.toString()} onValueChange={(value) => updateFilter('distance', parseInt(value))}>
                  <SelectTrigger className="w-full sm:w-32 md:w-36 h-8 text-sm">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent className="z-50">
                    {distanceOptions.map(option => (
                      <SelectItem key={option.value} value={option.value.toString()}>
                        {option.label}
                      </SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Category Pills */}
      <div className="flex gap-2 overflow-x-auto pb-2">
        {categories.map(category => (
          <Badge
            key={category}
            variant={filters.category === category ? "default" : "outline"}
            className={`cursor-pointer whitespace-nowrap px-4 py-2 text-sm font-medium ${
              filters.category === category 
                ? "text-white hover:opacity-90" 
                : "hover:bg-[#efe7dd]"
            }`}
            onClick={() => updateFilter('category', category)}
          >
            {category}
          </Badge>
        ))}
      </div>

      {/* Advanced Filters Panel */}
      {showAdvancedFilters && (
        <div className="bg-[#fbf8f4] rounded-lg border border-[#efe7dd] p-6 space-y-6">
          <div className="flex items-center justify-between">
            <h3 className="text-lg font-medium text-[#837069]">Geavanceerde Filters</h3>
            <Button 
              variant="ghost" 
              size="sm" 
              onClick={clearFilters}
              className="text-[#837069] hover:text-[#837069] hover:bg-[#efe7dd]"
            >
              <X className="h-4 w-4 mr-1" />
              Wis alles
            </Button>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
            {/* Price Range */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Euro className="h-4 w-4 text-[#837069]" />
                <label className="font-medium text-[#837069]">Prijsbereik</label>
              </div>
              <div className="space-y-2">
                <Slider
                  value={filters.priceRange}
                  onValueChange={(value) => updateFilter('priceRange', value as [number, number])}
                  max={500}
                  min={0}
                  step={5}
                  className="w-full"
                />
                <div className="flex justify-between text-sm text-[#b6a092]">
                  <span>€{filters.priceRange[0]}</span>
                  <span>€{filters.priceRange[1]}+</span>
                </div>
              </div>
            </div>

            {/* Minimum Rating */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Star className="h-4 w-4 text-[#837069]" />
                <label className="font-medium text-[#837069]">Minimum Rating</label>
              </div>
              <div className="space-y-2">
                {[0, 3, 4, 4.5].map(rating => (
                  <div key={rating} className="flex items-center space-x-2">
                    <Checkbox
                      id={`rating-${rating}`}
                      checked={filters.minRating === rating}
                      onCheckedChange={() => updateFilter('minRating', rating)}
                    />
                    <label 
                      htmlFor={`rating-${rating}`} 
                      className="text-sm text-[#837069] flex items-center gap-1 cursor-pointer"
                    >
                      {rating === 0 ? 'Alle ratings' : (
                        <>
                          {rating}+ <Star className="h-3 w-3 fill-yellow-400 text-yellow-400" />
                        </>
                      )}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Belgian Locations */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <MapPin className="h-4 w-4 text-[#837069]" />
                <label className="font-medium text-[#837069]">Belgische Regio's</label>
              </div>
              <div className="space-y-2 max-h-32 overflow-y-auto">
                {belgianLocations.map(location => (
                  <div key={location} className="flex items-center space-x-2">
                    <Checkbox
                      id={location}
                      checked={filters.locations.includes(location)}
                      onCheckedChange={() => toggleLocation(location)}
                    />
                    <label htmlFor={location} className="text-sm text-[#837069] cursor-pointer">
                      {location}
                    </label>
                  </div>
                ))}
              </div>
            </div>

            {/* Availability */}
            <div className="space-y-3">
              <div className="flex items-center gap-2">
                <Calendar className="h-4 w-4 text-[#837069]" />
                <label className="font-medium text-[#837069]">
                  {currentTab === 'services' ? 'Beschikbaarheid' : 'Voorraad & Levering'}
                </label>
              </div>
              <div className="space-y-2">
                {availabilityOptions.map(option => (
                  <div key={option} className="flex items-center space-x-2">
                    <Checkbox
                      id={option}
                      checked={filters.availability.includes(option)}
                      onCheckedChange={() => toggleAvailability(option)}
                    />
                    <label htmlFor={option} className="text-sm text-[#837069] cursor-pointer">
                      {option}
                    </label>
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Active Filters & Results with Belgian Context */}
      <div className="flex items-center justify-between bg-[#fbf8f4] rounded-lg p-4 border border-[#efe7dd]">
        <div className="flex items-center gap-3">
          <span className="text-sm font-medium text-[#837069]">
            {resultsCount} resultaten in België
          </span>
          {/* Enhanced Active Filter Chips */}
          <div className="flex items-center gap-2">
            {filters.search && (
              <Badge variant="secondary" className="bg-[#efe7dd] text-[#837069]">
                🔍 "{filters.search}"
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => updateFilter('search', '')}
                />
              </Badge>
            )}
            {filters.userLocation && (
              <Badge variant="secondary" className="bg-[#efe7dd] text-[#837069]">
                📍 {filters.userLocation} {filters.distance > 0 && `(${filters.distance}km)`}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => {
                    updateFilter('userLocation', '');
                    updateFilter('distance', 0);
                  }}
                />
              </Badge>
            )}
            {filters.category !== 'Alle categorieën' && (
              <Badge variant="secondary" className="bg-[#efe7dd] text-[#837069]">
                🏷️ {filters.category}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => updateFilter('category', 'Alle categorieën')}
                />
              </Badge>
            )}
            {filters.locations.map(location => (
              <Badge key={location} variant="secondary" className="bg-[#efe7dd] text-[#837069]">
                🏛️ {location}
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => toggleLocation(location)}
                />
              </Badge>
            ))}
            {filters.minRating > 0 && (
              <Badge variant="secondary" className="bg-[#efe7dd] text-[#837069]">
                ⭐ {filters.minRating}+ rating
                <X 
                  className="h-3 w-3 ml-1 cursor-pointer" 
                  onClick={() => updateFilter('minRating', 0)}
                />
              </Badge>
            )}
          </div>
        </div>

        {activeFiltersCount > 0 && (
          <Button 
            variant="ghost" 
            size="sm" 
            onClick={clearFilters}
            className="text-[#837069] hover:text-[#837069] hover:bg-[#efe7dd]"
          >
            Wis alle filters
          </Button>
        )}
      </div>
    </div>
  );
};

export default SearchAndFilters;